import type { QuestionModel } from '@shared/models/Question';
import { Avatar, Box, Typography } from '@mui/material';
import moment from 'moment';
import React, { forwardRef, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from '@shared/components/Buttons/Button';
import { UserContext } from '@/shared/contexts/UserContext';
import { useReviewContext } from '../../review/context/ReviewContext';
import AnswerCard from './AnswerCard';

interface QuestionCardProps {
  question: QuestionModel;
  onAnswer?: (questionId: number) => void;
  shouldHighlight?: boolean;
  showAnswerButton?: boolean;
}

const Card = styled(Box)<{ $isHighlighted?: boolean }>`
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    border-color: rgba(11, 116, 218, 0.2);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  }

  ${props => props.$isHighlighted && `
    animation: highlightPulse 2s ease-in-out;
    border-color: rgba(43, 144, 227, 0.6);
    box-shadow: 0 0 0 4px rgba(43, 144, 227, 0.1), 0 8px 32px rgba(43, 144, 227, 0.15);
  `}

  @keyframes highlightPulse {
    0% {
      box-shadow: 0 0 0 0 rgba(43, 144, 227, 0.4), 0 4px 20px rgba(0, 0, 0, 0.08);
      border-color: rgba(43, 144, 227, 0.8);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(43, 144, 227, 0.1), 0 8px 32px rgba(43, 144, 227, 0.2);
      border-color: rgba(43, 144, 227, 0.6);
    }
    100% {
      box-shadow: 0 0 0 4px rgba(43, 144, 227, 0.1), 0 8px 32px rgba(43, 144, 227, 0.15);
      border-color: rgba(43, 144, 227, 0.6);
    }
  }
`;

const QuestionSection = styled(Box)`
  padding: 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
`;

const QuestionHeader = styled(Box)`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
`;

const AuthorAvatar = styled(Avatar)`
  width: 48px;
  height: 48px;
  font-weight: 600;
  font-size: 1.125rem;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const QuestionContent = styled(Box)`
  flex: 1;
  min-width: 0;
`;

const QuestionText = styled(Typography)`
  font-size: 1.125rem;
  font-weight: 500;
  line-height: 1.7;
  color: rgba(0, 0, 0, 0.9);
  margin-top: 4px;
  letter-spacing: 0.01em;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const QuestionMeta = styled(Box)`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const AuthorName = styled(Typography)`
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.7);
`;

const Timestamp = styled(Typography)`
  font-size: 0.8125rem;
  color: rgba(0, 0, 0, 0.5);

  &:before {
    content: '•';
    margin: 0 8px;
  }
`;

const QuestionLabel = styled(Typography)`
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(11, 116, 218, 0.7);
  margin-top: 12px;
  margin-bottom: 4px;
`;

const ActionBar = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px 24px;
  background: rgba(0, 0, 0, 0.02);
  gap: 12px;
`;

const AnswerSection = styled(Box)`
  padding: 0 24px 24px 24px;
  background: rgba(249, 250, 251, 0.5);
`;

const QuestionCard = forwardRef<HTMLDivElement, QuestionCardProps>(({ question, onAnswer, shouldHighlight, showAnswerButton = true }, ref) => {
  const { user } = useContext(UserContext);
  const { currentReview } = useReviewContext();
  const [isHighlighted, setIsHighlighted] = useState(false);

  useEffect(() => {
    if (shouldHighlight) {
      // Add a small delay before highlighting for better UX
      const timer = setTimeout(() => {
        setIsHighlighted(true);
        // Remove highlight after animation completes
        setTimeout(() => setIsHighlighted(false), 3000);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [shouldHighlight]);

  const formatDate = (date: string | Date) => {
    return moment(date).fromNow();
  };

  // Get initials for avatar
  const getAuthorInitials = () => {
    if (question.user?.username) {
      const words = question.user.username.match(/[A-Z][a-z]+/g) || [];
      if (words.length >= 2) {
        return (words[0]?.[0] || '') + (words[1]?.[0] || '');
      }
      return question.user.username.substring(0, 2).toUpperCase();
    }
    if (question.user?.firstName && question.user?.lastName) {
      return `${question.user.firstName.charAt(0)}${question.user.lastName.charAt(0)}`.toUpperCase();
    }
    return 'U';
  };

  // Get display name for author
  const getAuthorName = () => {
    if (question.user?.username) {
      return question.user.username;
    }
    if (question.user?.firstName && question.user?.lastName) {
      return `${question.user.firstName} ${question.user.lastName}`;
    }
    return 'Anonymous';
  };

  const avatarColor = question.user?.avatarColor || '#0b74da';
  const isReviewAuthor = user?.id === currentReview?.userId;
  const hasAnswer = !!question.answer;

  return (
    <Card ref={ref} data-testid="question-card" $isHighlighted={isHighlighted}>
      <QuestionSection>
        <QuestionHeader>
          <AuthorAvatar sx={{ bgcolor: avatarColor }}>
            {getAuthorInitials()}
          </AuthorAvatar>
          <QuestionContent>
            <QuestionMeta>
              <AuthorName>{getAuthorName()}</AuthorName>
              <Timestamp>{formatDate(question.createdAt)}</Timestamp>
            </QuestionMeta>
            <QuestionLabel>Question</QuestionLabel>
            <QuestionText>{question.question}</QuestionText>
          </QuestionContent>
        </QuestionHeader>
      </QuestionSection>

      {hasAnswer && question.answer && (
        <AnswerSection>
          <Box sx={{ mt: 2 }}>
            <AnswerCard answer={question.answer} />
          </Box>
        </AnswerSection>
      )}

      {!hasAnswer && isReviewAuthor && showAnswerButton && (
        <ActionBar>
          <Button
            data-testid="answer-question-button"
            size="small"
            variantType="success"
            onClick={() => onAnswer?.(question.id)}
            sx={{
              'background': 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
              'color': 'white',
              'fontWeight': 600,
              'fontSize': '0.875rem',
              'padding': '8px 20px',
              'boxShadow': '0 2px 8px rgba(76, 175, 80, 0.2)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              },
            }}
          >
            Answer Question
          </Button>
        </ActionBar>
      )}
    </Card>
  );
});

QuestionCard.displayName = 'QuestionCard';

export default QuestionCard;
